/**
 * Turns voice-service data-channel events into ephemeral live-caption state.
 *
 * Handles the gateway's `voice_gateway.event` messages and standard RTVI
 * (`label: "rtvi-ai"`) messages. Unknown events are ignored. Nothing here is
 * persisted: the saved conversation remains the source of truth.
 */

export const EMPTY_LIVE = Object.freeze({
  turn: null,
  userText: '',
  userFinal: false,
  assistantSentences: [],
  step: null,
  errorCode: null,
  done: false,
  /** After EAP_TIMEOUT: null | 'pending' | 'found' | 'missing' (saved answer lookup). */
  answerCheck: null,
  /** Last spoken command the voice service handled itself: null | 'hold' | 'continue' | 'stop' | 'repeat'. */
  command: null,
  /** "Hold on": the answer is kept and waits for "continue". */
  paused: false,
  /** Short reply the voice service spoke for a command (e.g. "There's nothing to repeat yet."). */
  reply: null,
  /** Speech the voice service did not treat as a question: null | { reason: 'filler' | 'barge_in_disabled', text }. */
  ignored: null,
  /** Question text the voice service sent to the knowledge service for this turn. */
  question: '',
  /** Captions from before the latest utterance, restored when that utterance turns out to be a command. */
  prior: null,
});

/** Phase ids match VOICE_PHASE values in useVoiceSession. */
const GATEWAY_STATE_PHASE = {
  listening: 'listening',
  user_speaking: 'speaking',
  transcribing: 'processing',
  processing: 'processing',
  speaking: 'assistant_speaking',
  paused: 'paused',
};

const COMMAND_ALIASES = {
  hold: 'hold',
  hold_on: 'hold',
  pause: 'hold',
  paused: 'hold',
  wait: 'hold',
  continue: 'continue',
  resume: 'continue',
  go_on: 'continue',
  stop: 'stop',
  cancel: 'stop',
  repeat: 'repeat',
  say_again: 'repeat',
};

/** Field names are read leniently so minor gateway payload changes don't break captions. */
const normalizeCommand = (event) => {
  const raw = [event.command, event.name, event.action, event.intent, event.data?.command].find(
    (v) => typeof v === 'string' && v.trim(),
  );
  if (!raw) return null;
  const key = raw.trim().toLowerCase().replace(/[^a-z]+/g, '_').replace(/^_+|_+$/g, '');
  return COMMAND_ALIASES[key] || null;
};

const replyText = (event) =>
  cleanText([event.text, event.message, event.reply, event.data?.text].find((v) => typeof v === 'string'));

const eventKind = (event) => {
  if (event.type === 'voice_gateway.event') return event.stage;
  if (event.type === 'voice_command' || event.type === 'voice_reply' || event.type === 'microphone') return event.type;
  return null;
};

/** Gateway-reported mute state from a `microphone` event, or null when the payload has none. */
const microphoneMuted = (event) => {
  for (const v of [event.muted, event.data?.muted]) if (typeof v === 'boolean') return v;
  if (event.state === 'muted') return true;
  if (event.state === 'unmuted') return false;
  return null;
};

const RTVI_PHASE = {
  'user-started-speaking': 'speaking',
  'user-stopped-speaking': 'listening',
  'bot-llm-started': 'processing',
  'bot-started-speaking': 'assistant_speaking',
  'bot-stopped-speaking': 'listening',
};

export const VOICE_TURN_ERROR_MESSAGES = {
  EAP_TIMEOUT: 'The knowledge service took too long to respond.',
};
export const voiceTurnErrorMessage = (code) =>
  VOICE_TURN_ERROR_MESSAGES[code] || "The assistant couldn't answer this turn.";

const cleanText = (value) => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '');

/** True while the turn holds nothing but what the user is saying. */
const utteranceOnly = (live) =>
  !live.done &&
  !live.assistantSentences.length &&
  !live.step &&
  !live.errorCode &&
  !live.reply &&
  !live.command &&
  !live.ignored;

const snapshot = (live) => (utteranceOnly(live) ? live.prior : { ...live, prior: null });

const startTurn = (turn, from) => ({ ...EMPTY_LIVE, turn: turn ?? null, prior: from ? snapshot(from) : null });

/** Put back the question captions an utterance overwrote once the voice service says it was not a question. */
const restoreQuestion = (live) => {
  if (live.prior && utteranceOnly(live)) return { ...live.prior, prior: null };
  if (live.question && live.userText !== live.question) return { ...live, userText: live.question, userFinal: true, prior: null };
  return { ...live, prior: null };
};

const applyCommand = (live, command) => {
  const next = { ...restoreQuestion(live), command, paused: command === 'hold', reply: null, ignored: null };
  if (command === 'stop') return { ...next, assistantSentences: [], step: null };
  return next;
};

const answerText = (live) => live.assistantSentences.join(' ');

/** Add replayed answer text to the captions without repeating the parts already shown. */
const appendAnswerText = (live, text) => {
  let rest = text;
  for (let found = true; found && rest; ) {
    const known = live.assistantSentences.find((s) => rest.startsWith(s));
    found = Boolean(known);
    if (known) rest = rest.slice(known.length).trim();
  }
  if (!rest || answerText(live).includes(rest)) return live;
  return { ...live, assistantSentences: [...live.assistantSentences, rest] };
};

const REPLAY_SOURCES = new Set(['command_continue', 'command_repeat']);

/**
 * `questionSent` marks the moment the voice service hands a question to the knowledge service.
 * `answerPreview` is set when caption text arrives (often with spoken:false before TTS plays).
 * @returns {{ live: object, phase: string|null, turnComplete: boolean, command: string|null, muted: boolean|null, questionSent: boolean, answerPreview: boolean, turnFailed: boolean }}
 */
export function reduceVoiceEvent(live, event) {
  const out = {
    live,
    phase: null,
    turnComplete: false,
    command: null,
    muted: null,
    questionSent: false,
    answerPreview: false,
    turnFailed: false,
  };
  if (!event || typeof event !== 'object') return out;
  const result = (r) => {
    if (r.live.paused && r.phase === 'listening') r.phase = 'paused';
    return r;
  };

  const kind = eventKind(event);
  if (kind) {
    switch (kind) {
      case 'status': {
        out.phase = GATEWAY_STATE_PHASE[event.state] || null;
        if (event.state === 'user_speaking' && live.done) out.live = startTurn(null, live);
        if (event.state === 'paused') out.live = { ...live, paused: true };
        if (event.state === 'complete') {
          out.live = { ...live, step: null, done: true };
          out.turnComplete = true;
        }
        return result(out);
      }
      case 'voice_command': {
        const command = normalizeCommand(event);
        if (!command) return out;
        out.command = command;
        out.live = applyCommand(live, command);
        if (command === 'hold') out.phase = 'paused';
        return out;
      }
      case 'microphone': {
        out.muted = microphoneMuted(event);
        return out;
      }
      case 'voice_reply': {
        const text = replyText(event);
        if (!text) return out;
        // Continue/repeat replies carry answer text (only the unheard part when resuming).
        out.live = REPLAY_SOURCES.has(event.source) ? appendAnswerText({ ...live, reply: null }, text) : { ...live, reply: text };
        return out;
      }
      case 'ignored_speech': {
        const reason = event.reason === 'barge_in_disabled' ? 'barge_in_disabled' : 'filler';
        out.live = { ...restoreQuestion(live), ignored: { reason, text: cleanText(event.text) } };
        return out;
      }
      case 'user_transcript': {
        const text = cleanText(event.text);
        if (!text) return out;
        const sameTurn = live.turn != null && live.turn === event.turn && !live.done;
        const base = sameTurn ? live : startTurn(event.turn, live);
        out.live = {
          ...base,
          turn: event.turn ?? null,
          userText: text,
          userFinal: true,
          question: base.question || text,
        };
        out.questionSent = true;
        return out;
      }
      case 'eap_progress': {
        const label = cleanText(event.step?.label);
        if (!label) return out;
        if (event.step?.status === 'running') out.live = { ...live, step: label };
        return out;
      }
      case 'error': {
        const errorCode = typeof event.error_code === 'string' ? event.error_code : 'UNKNOWN';
        out.live = { ...live, step: null, errorCode, answerCheck: errorCode === 'EAP_TIMEOUT' ? 'pending' : null };
        out.turnFailed = true;
        return out;
      }
      default:
        return out;
    }
  }

  if (event.label === 'rtvi-ai') {
    out.phase = RTVI_PHASE[event.type] || null;
    if (event.type === 'user-transcription') {
      const text = cleanText(event.data?.text);
      if (!text) return result(out);
      // New speech after an answer (or a finished/failed turn) must not keep the prior captions.
      const base =
        live.done || live.errorCode || live.assistantSentences.length > 0
          ? startTurn(null, live)
          : live;
      if (base.userFinal && !event.data?.final) return result(out);
      out.live = { ...base, userText: text, userFinal: Boolean(event.data?.final) || base.userFinal };
      return result(out);
    }
    if (event.type === 'bot-output') {
      const text = cleanText(event.data?.text);
      // After "stop" the discarded answer must not come back through late caption events.
      if (!text || text === live.reply || live.command === 'stop' || answerText(live).includes(text)) {
        return result(out);
      }
      // Late TTS captions from the previous turn must not attach to the new question.
      const priorAnswer = live.prior ? answerText(live.prior) : '';
      if (priorAnswer && (priorAnswer.includes(text) || text.includes(priorAnswer))) {
        return result(out);
      }
      // No EAP question for this turn yet — ignore leftover speech from the prior answer.
      if (!live.question && live.command !== 'continue' && live.command !== 'repeat') {
        return result(out);
      }
      out.live = { ...live, assistantSentences: [...live.assistantSentences, text] };
      // Prefer spoken:false (full text before TTS); also stream later caption chunks into chat.
      out.answerPreview = true;
      return result(out);
    }
    return result(out);
  }

  return out;
}
